type AnalysisContentSnapshot = {
  detailNarrative?: string;
  photoObservations?: string[];
  dataAgreement?: string[];
  scenarios?: Array<{ title?: string; explanation?: string; likelihood?: string }>;
  outlook?: string;
  nextObservations?: string[];
};

let latestContent: AnalysisContentSnapshot | null = null;
let elevationOverrideM: number | null = null;

export function setLatestAnalysisContent(content: AnalysisContentSnapshot | null): void {
  latestContent = content;
  queueMicrotask(enhanceResultView);
}

function installStyles(): void {
  const style = document.createElement("style");
  style.textContent = `
    /* Preview editors: use the same centered popup treatment for every editable item. */
    .place-search-wrap,
    .time-editor,
    .heading-editor,
    .elevation-editor {
      position: fixed !important;
      z-index: 90 !important;
      top: 50% !important;
      left: 50% !important;
      width: min(calc(100vw - 32px), 500px) !important;
      max-height: min(76dvh, 620px) !important;
      overflow-y: auto !important;
      margin: 0 !important;
      padding: 22px 18px 18px !important;
      transform: translate(-50%, -50%) !important;
      border: 1px solid rgba(36, 91, 105, .16) !important;
      border-radius: 24px !important;
      background: #f9fdfd !important;
      box-shadow: 0 0 0 100vmax rgba(12,35,42,.46), 0 22px 70px rgba(8,34,42,.28) !important;
    }
    .place-search-wrap::before {
      display: block;
      margin: 0 0 14px;
      color: #183039;
      font-size: 1.06rem;
      font-weight: 800;
      content: "場所を変更";
    }
    .place-search-wrap .text-button { margin-top: 16px; }
    .place-search-wrap .place-results { max-height: 36dvh; overflow-y: auto; }

    .context-elevation > button {
      display: grid;
      grid-template-columns: minmax(0, 1fr) auto;
      align-items: center;
      gap: 5px;
      width: 100%;
      min-height: 30px;
      border: 0;
      padding: 0;
      background: transparent;
      color: var(--ink);
      text-align: left;
      cursor: pointer;
    }
    .context-elevation > button small {
      color: var(--sky-dark);
      font-size: .65rem;
      font-weight: 800;
    }
    .elevation-editor label {
      display: block;
      margin-bottom: 7px;
      font-size: .78rem;
      font-weight: 800;
    }
    .elevation-editor input {
      width: 100%;
      min-height: 48px;
      border: 1px solid #b9d5dc;
      border-radius: 13px;
      padding: 8px 10px;
      background: white;
      color: var(--ink);
    }
    .elevation-editor .editor-actions {
      display: grid;
      grid-template-columns: 1fr 1.45fr;
      gap: 8px;
      margin-top: 9px;
    }
    .elevation-editor button {
      min-height: 44px;
      border: 0;
      border-radius: 12px;
      background: #dcebed;
      color: #315e68;
      font-weight: 800;
      cursor: pointer;
    }
    .elevation-editor button[type="submit"] { background: var(--sky-dark); color: white; }
    .elevation-editor > p { margin: 9px 2px 0; color: var(--muted); font-size: .7rem; line-height: 1.55; }

    /* Bottom navigation should use a consistent line-icon language, never emoji artwork. */
    .bottom-nav .settings-line-icon {
      display: block;
      width: 1.45rem;
      height: 1.45rem;
      margin: 0 auto 2px;
      fill: none;
      stroke: currentColor;
      stroke-width: 2.1;
      stroke-linecap: round;
      stroke-linejoin: round;
    }

    /* Detailed view is one continuous reading. */
    .unified-detail-card {
      margin-top: 0;
      padding: 24px 22px;
      border: 1px solid rgba(36, 91, 105, .14);
      border-radius: 24px;
      background: rgba(255,255,255,.9);
      box-shadow: 0 18px 48px rgba(32,81,94,.08);
    }
    .unified-detail-kicker {
      display: block;
      margin-bottom: 6px;
      color: #1985a5;
      font-size: .73rem;
      font-weight: 800;
      letter-spacing: .05em;
    }
    .unified-detail-card h2 {
      margin: 0 0 18px;
      color: #183039;
      font-size: 1.28rem;
    }
    .unified-detail-body p {
      margin: 0 0 1.15em;
      color: #405f68;
      font-size: .96rem;
      line-height: 1.95;
    }
    .unified-detail-body p:last-child { margin-bottom: 0; }

    /* Confidence is internal support information, not part of the reading UI. */
    .confidence,
    .confidence-card { display: none !important; }

    /* Keep the safety note present but quiet. */
    .caution-card.compact-caution {
      margin: 18px 4px 0 !important;
      padding: 0 !important;
      border: 0 !important;
      border-radius: 0 !important;
      background: transparent !important;
      box-shadow: none !important;
    }
    .caution-card.compact-caution h2 { display: none !important; }
    .caution-card.compact-caution p {
      margin: 0 !important;
      color: #74878d !important;
      font-size: .68rem !important;
      line-height: 1.6 !important;
    }

    @media (max-width: 520px) {
      .place-search-wrap,
      .time-editor,
      .heading-editor,
      .elevation-editor { width: calc(100vw - 28px) !important; padding: 20px 16px 16px !important; }
      .unified-detail-card { padding: 21px 18px; }
      .unified-detail-body p { font-size: .94rem; line-height: 1.9; }
    }
  `;
  document.head.appendChild(style);
}

function requestPath(input: RequestInfo | URL): string {
  try {
    if (input instanceof Request) return new URL(input.url, location.href).pathname;
    return new URL(String(input), location.href).pathname;
  } catch {
    return "";
  }
}

function withElevationOverride(input: RequestInfo | URL, init?: RequestInit): RequestInit | undefined {
  if (requestPath(input) !== "/api/analyze" || elevationOverrideM === null || typeof init?.body !== "string") return init;
  try {
    const body = JSON.parse(init.body) as { context?: { elevationM?: number | null } };
    if (!body.context) return init;
    body.context.elevationM = elevationOverrideM;
    return { ...init, body: JSON.stringify(body) };
  } catch {
    return init;
  }
}

function interceptAnalyzeResponse(): void {
  const originalFetch = window.fetch.bind(window);
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const nextInit = withElevationOverride(input, init);
    const response = await originalFetch(input, nextInit);
    if (requestPath(input) === "/api/analyze" && response.ok) {
      void response
        .clone()
        .json()
        .then((data) => {
          const parsed = data as { content?: AnalysisContentSnapshot };
          setLatestAnalysisContent(parsed.content ?? null);
        })
        .catch(() => undefined);
    }
    return response;
  };
}

function fallbackNarrative(content: AnalysisContentSnapshot): string {
  const scenarios = (content.scenarios ?? [])
    .filter((scenario) => scenario.likelihood !== "低")
    .map((scenario) => [scenario.title, scenario.explanation].filter(Boolean).join("："));
  return [
    ...(content.photoObservations ?? []),
    ...(content.dataAgreement ?? []),
    ...scenarios,
    content.outlook ?? "",
  ]
    .filter(Boolean)
    .join("\n\n");
}

function narrativeParagraphs(text: string): string[] {
  const cleaned = text
    .replace(/^#{1,6}\s*/gm, "")
    .replace(/\*\*(.*?)\*\*/g, "$1")
    .replace(/^[-*]\s+/gm, "")
    .trim();
  const blocks = cleaned.split(/\n{2,}/).map((part) => part.trim()).filter(Boolean);
  if (blocks.length >= 2) return blocks;

  const sentences = cleaned.match(/[^。！？]+[。！？]+/g)?.map((part) => part.trim()).filter(Boolean) ?? [cleaned];
  if (sentences.length <= 3) return sentences;
  const paragraphs: string[] = [];
  for (let i = 0; i < sentences.length; i += 2) paragraphs.push(sentences.slice(i, i + 2).join(""));
  return paragraphs;
}

function makeTextElement(tag: "p" | "li", text: string): HTMLElement {
  const element = document.createElement(tag);
  element.textContent = text;
  return element;
}

function enhanceDetailView(): void {
  const detailButton = document.querySelector<HTMLButtonElement>('button[data-action="mode"][data-mode="detail"]');
  if (!detailButton || !detailButton.classList.contains("active")) return;
  const container = document.querySelector<HTMLElement>(".result-content");
  if (!container || container.dataset.unifiedDetail === "1") return;

  const content = latestContent;
  const narrative = content ? (content.detailNarrative?.trim() || fallbackNarrative(content)) : "";
  if (!narrative) return;

  container.replaceChildren();
  container.dataset.unifiedDetail = "1";

  const article = document.createElement("section");
  article.className = "unified-detail-card";
  const kicker = document.createElement("span");
  kicker.className = "unified-detail-kicker";
  kicker.textContent = "写真とデータを、ひとつにつないで";
  const heading = document.createElement("h2");
  heading.textContent = "今日の空を読む";
  const body = document.createElement("div");
  body.className = "unified-detail-body";
  for (const paragraph of narrativeParagraphs(narrative)) body.appendChild(makeTextElement("p", paragraph));
  article.appendChild(kicker);
  article.appendChild(heading);
  article.appendChild(body);
  container.appendChild(article);
}

function compactSafetyNote(): void {
  const caution = document.querySelector<HTMLElement>(".caution-card");
  if (!caution || caution.dataset.compact === "1") return;
  caution.dataset.compact = "1";
  caution.classList.add("compact-caution");
  caution.replaceChildren();
  const note = document.createElement("p");
  note.textContent = "※ 防災情報は、気象庁などの公式情報をご確認ください。";
  caution.appendChild(note);
}

function enhanceSettingsIcon(): void {
  const button = document.querySelector<HTMLButtonElement>('.bottom-nav button[data-action="nav-settings"]');
  if (!button || button.dataset.lineIcon === "1") return;
  const icon = button.querySelector<HTMLElement>("span[aria-hidden='true']");
  if (!icon) return;
  icon.outerHTML = `
    <svg class="settings-line-icon" viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="3"></circle>
      <path d="M19.4 15a1.7 1.7 0 0 0 .34 1.88l.06.06-2.83 2.83-.06-.06A1.7 1.7 0 0 0 15 19.4a1.7 1.7 0 0 0-1 .6 1.7 1.7 0 0 0-.4 1.1V21h-4v-.09A1.7 1.7 0 0 0 9 19.4a1.7 1.7 0 0 0-1.88.34l-.06.06-2.83-2.83.06-.06A1.7 1.7 0 0 0 4.6 15a1.7 1.7 0 0 0-.6-1 1.7 1.7 0 0 0-1.1-.4H3v-4h.09A1.7 1.7 0 0 0 4.6 9a1.7 1.7 0 0 0-.34-1.88l-.06-.06 2.83-2.83.06.06A1.7 1.7 0 0 0 9 4.6a1.7 1.7 0 0 0 1-.6 1.7 1.7 0 0 0 .4-1.1V3h4v.09A1.7 1.7 0 0 0 15 4.6a1.7 1.7 0 0 0 1.88-.34l.06-.06 2.83 2.83-.06.06A1.7 1.7 0 0 0 19.4 9c.16.37.36.7.6 1 .3.34.7.54 1.1.6H21v4h-.09a1.7 1.7 0 0 0-1.51.4Z"></path>
    </svg>`;
  button.dataset.lineIcon = "1";
}

function parseElevationLabel(text: string): number | null {
  const match = text.match(/-?\d+(?:\.\d+)?/);
  if (!match) return null;
  const value = Number(match[0]);
  return Number.isFinite(value) ? value : null;
}

function closeElevationEditor(): void {
  document.querySelector(".elevation-editor")?.remove();
}

function openElevationEditor(): void {
  closeElevationEditor();
  const valueNode = document.querySelector<HTMLElement>(".context-elevation strong");
  const current = elevationOverrideM ?? parseElevationLabel(valueNode?.textContent ?? "");
  const editor = document.createElement("section");
  editor.className = "elevation-editor";
  editor.innerHTML = `
    <form id="elevation-setting">
      <label for="elevation-value">地点標高（m）</label>
      <input id="elevation-value" name="elevation" type="number" step="1" min="-500" max="9000" value="${current ?? ""}" placeholder="例：110" required>
      <div class="editor-actions"><button type="button" data-elevation-cancel>キャンセル</button><button type="submit">この標高に設定</button></div>
    </form>
    <p>撮影地点のおおよその標高で大丈夫です。気象データとの比較に使います。</p>`;
  document.body.appendChild(editor);
  editor.querySelector<HTMLInputElement>("#elevation-value")?.focus();
}

function enhanceElevationEditor(): void {
  const contextCard = document.querySelector<HTMLElement>(".context-card");
  if (!contextCard) return;
  const cell = contextCard.children.item(3) as HTMLElement | null;
  if (!cell || cell.classList.contains("context-elevation")) return;
  const label = cell.querySelector("span")?.textContent?.trim();
  if (label !== "地点標高") return;
  const strong = cell.querySelector("strong");
  const shown = elevationOverrideM === null ? (strong?.textContent ?? "不明") : `約${Math.round(elevationOverrideM)}m`;
  cell.classList.add("context-elevation");
  cell.innerHTML = `<span>地点標高</span><button type="button" data-elevation-edit><strong>${shown}</strong><small>変更</small></button>`;
}

function enhanceResultView(): void {
  compactSafetyNote();
  enhanceDetailView();
}

function installElevationEvents(): void {
  document.addEventListener("click", (event) => {
    const target = event.target as HTMLElement;
    if (target.closest("[data-elevation-edit]")) {
      openElevationEditor();
      return;
    }
    if (target.closest("[data-elevation-cancel]")) closeElevationEditor();
  });

  document.addEventListener("submit", (event) => {
    const form = event.target as HTMLFormElement;
    if (form.id !== "elevation-setting") return;
    event.preventDefault();
    const value = Number(new FormData(form).get("elevation"));
    if (!Number.isFinite(value) || value < -500 || value > 9000) return;
    elevationOverrideM = value;
    const strong = document.querySelector<HTMLElement>(".context-elevation strong");
    if (strong) strong.textContent = `約${Math.round(value)}m`;
    closeElevationEditor();
  });
}

function observeRenders(): void {
  const observer = new MutationObserver(() => {
    enhanceElevationEditor();
    enhanceResultView();
    enhanceSettingsIcon();
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });
}

installStyles();
installElevationEvents();
interceptAnalyzeResponse();
observeRenders();
enhanceElevationEditor();
enhanceResultView();
enhanceSettingsIcon();
